import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon3';
import type { EventClickArg, EventDropArg, EventMountArg } from '@fullcalendar/core';
import type { EventDragStopArg, EventResizeDoneArg } from '@fullcalendar/interaction';

import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import { SNAP_MINUTES } from '@/lib/motion';
import { useCurrentUser } from '@/features/auth/useAuth';
import { blockerTitle, type BusyInterval, type TimeBlock } from '@/features/calendars/types';
import { afterCalendarGesture, CALENDAR_DRAG_PROPS } from '@/features/calendars/calendarDrag';
import { blockTitle } from '@/features/calendars/scheduleConflict';
import { useStableCalendarEvents } from '@/features/calendars/useStableCalendarEvents';
import { eventShowsAsBusy, type PlanEvent } from '@/features/calendars/PlanEventSheet';
import { useTimeBlocks } from '@/features/calendars/useTimeBlocks';
import { useBlockers } from '@/features/calendars/useBlockers';
import { minutesToSlot, planDayWindow } from '@/features/calendars/dayWindow';
import { useWorkBlocks } from '@/features/time/useWorkBlocks';
import { useClients } from '@/features/tasks/useTasks';
import { todayRange } from './greeting';

export interface DayCalendarScheduling {
  dragConflicts: boolean;
  onDrop: (info: { date: Date; draggedEl: HTMLElement; allDay: boolean }) => void;
  onEventDrop: (arg: EventDropArg) => void;
  onEventResize: (arg: EventResizeDoneArg) => void;
  onEventDrag: (arg: { event: { id: string; start: Date | null; end: Date | null } }) => void;
  onEventDragStart: (arg: { event: { id: string; start: Date | null; end: Date | null } }) => void;
  onEventDragStop: (info: EventDragStopArg) => void;
}

interface Props {
  onEventClick: (event: PlanEvent) => void;
  onBlockClick: (block: TimeBlock) => void;
  /** When set, blocks can move/resize and backlog chips can drop onto the grid. */
  scheduling?: DayCalendarScheduling;
  className?: string;
}

/**
 * Day calendar for the Daily dashboard, read-only by default, interactive when
 * scheduling handlers are provided.
 */
export function DayCalendar({ onEventClick, onBlockClick, scheduling, className }: Props) {
  const interactive = scheduling != null;
  const { data: user } = useCurrentUser();
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dayStart = user?.day_start_minute ?? 420;
  const dayEnd = user?.day_end_minute ?? 1200;

  const range = useMemo(() => todayRange(timezone), [timezone]);
  const dayWindow = useMemo(
    () => planDayWindow(timezone, dayStart, dayEnd),
    [timezone, dayStart, dayEnd],
  );

  const calendarRef = useRef<FullCalendar>(null);
  const blocks = useTimeBlocks(range);
  const blockers = useBlockers(range);
  const workBlocks = useWorkBlocks();
  const clients = useClients();

  const events = useQuery({
    queryKey: ['calendars', 'events', range.start.toISOString(), range.end.toISOString()],
    queryFn: () =>
      api<PlanEvent[]>(
        `/api/calendars/events?ends_after=${encodeURIComponent(range.start.toISOString())}` +
          `&starts_before=${encodeURIComponent(range.end.toISOString())}`,
      ),
  });

  const busy = useQuery({
    queryKey: ['calendars', 'busy', range.start.toISOString(), range.end.toISOString()],
    queryFn: () =>
      api<BusyInterval[]>(
        `/api/calendars/busy?start=${encodeURIComponent(range.start.toISOString())}` +
          `&end=${encodeURIComponent(range.end.toISOString())}`,
      ),
  });

  const eventById = useMemo(() => {
    const map = new Map<number, PlanEvent>();
    for (const e of events.data ?? []) map.set(e.id, e);
    return map;
  }, [events.data]);

  const blockById = useMemo(() => {
    const map = new Map<number, TimeBlock>();
    for (const b of blocks.data ?? []) map.set(b.id, b);
    return map;
  }, [blocks.data]);

  const clientColorById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of clients.data ?? []) map.set(c.id, c.color);
    return map;
  }, [clients.data]);

  const fcEvents = useMemo(() => {
    // Free-showing events aren't time spent, the Daily calendar is a picture
    // of the day's busy time, not a copy of every synced calendar entry.
    const fromEvents = (events.data ?? [])
      .filter(eventShowsAsBusy)
      .map((e) => {
        const classes: string[] = [];
        // An appointment you ticked off reads the same as finished work: still
        // on the day, visibly spent.
        if (e.completed_at) classes.push('chronos-block-done');
        return {
          id: `evt-${e.id}`,
          title: e.title,
          start: e.starts_at,
          end: e.ends_at,
          allDay: e.all_day,
          backgroundColor: e.color || '#36465f',
          borderColor: e.color || '#36465f',
          textColor: '#ffffff',
          classNames: classes,
          editable: false,
          startEditable: false,
          durationEditable: false,
        };
      });

    const fromBlocks = (blocks.data ?? []).map((b) => {
      const proposed = b.status === 'proposed';
      const classes = ['chronos-block'];
      if (proposed) classes.push('chronos-block-proposed');
      if (b.status === 'completed') classes.push('chronos-block-done');
      const canEdit =
        interactive && b.status !== 'completed' && b.status !== 'skipped';
      return {
        id: `block-${b.id}`,
        title: blockTitle(b),
        start: b.starts_at,
        end: b.ends_at,
        backgroundColor: b.client_color,
        borderColor: b.client_color,
        textColor: '#ffffff',
        classNames: classes,
        editable: canEdit,
        extendedProps: { kind: 'block', blockId: b.id, status: b.status },
      };
    });

    const fromBusy = (busy.data ?? []).map((b, i) => ({
      id: `busy-${i}`,
      start: b.starts_at,
      end: b.ends_at,
      display: 'background' as const,
      backgroundColor: 'color-mix(in oklch, var(--brand-secondary) 28%, transparent)',
    }));

    const fromBlockers = (blockers.data ?? []).map((b) => ({
      id: `blk-${b.id}`,
      title: blockerTitle(b),
      start: b.starts_at,
      end: b.ends_at,
      backgroundColor: 'transparent',
      borderColor: 'var(--border-strong)',
      textColor: 'var(--text-secondary)',
      classNames: ['chronos-blocker'],
      editable: false,
    }));

    // A work block is the sprint session the day's tasks were done inside,
    // shown as a coloured left-edge stripe behind everything else, never a
    // full tile, so it never fights the client-coloured blocks drawn on top.
    const fromWorkBlocks = (workBlocks.data ?? [])
      .filter((wb) => wb.status !== 'skipped')
      .map((wb) => ({
        id: `wb-${wb.id}`,
        start: wb.starts_at,
        end: wb.ends_at,
        display: 'background' as const,
        classNames: ['chronos-workblock'],
        extendedProps: {
          accent: (wb.client_id && clientColorById.get(wb.client_id)) || 'var(--accent)',
        },
      }));

    return [...fromWorkBlocks, ...fromBusy, ...fromEvents, ...fromBlockers, ...fromBlocks];
  }, [events.data, blocks.data, blockers.data, busy.data, workBlocks.data, clientColorById, interactive]);

  const { displayEvents, beginDrag, endDrag } = useStableCalendarEvents(fcEvents);

  const scrollToNow = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.scrollToTime(dayWindow.scrollTime);
  }, [dayWindow.scrollTime]);

  useEffect(() => {
    const t = window.setTimeout(scrollToNow, 80);
    return () => window.clearTimeout(t);
  }, [scrollToNow, fcEvents.length]);

  const onClick = useCallback(
    (arg: EventClickArg) => {
      const id = arg.event.id;
      if (id.startsWith('evt-')) {
        const evt = eventById.get(Number(id.slice(4)));
        if (evt) onEventClick(evt);
        return;
      }
      if (id.startsWith('block-')) {
        const block = blockById.get(Number(id.slice(6)));
        if (block) onBlockClick(block);
      }
    },
    [blockById, eventById, onBlockClick, onEventClick],
  );

  // Background events can't take a backgroundColor per-instance the way we
  // need (one accent stripe colour per work block), stash it as a CSS
  // custom property on the element instead.
  const onEventMount = useCallback((arg: EventMountArg) => {
    const accent = arg.event.extendedProps.accent as string | undefined;
    if (accent) arg.el.style.setProperty('--wb-accent', accent);
  }, []);

  const loading =
    (events.isPending && !events.data) ||
    (blocks.isPending && !blocks.data) ||
    (busy.isPending && !busy.data);
  const showHardError =
    (events.isError && !events.data) ||
    (blocks.isError && !blocks.data) ||
    (busy.isError && !busy.data);
  const itemCount = (blocks.data?.length ?? 0) + (events.data?.length ?? 0);

  const retryCalendar = () => {
    void events.refetch();
    void blocks.refetch();
    void busy.refetch();
  };

  return (
    <section
      className={cn(
        'flex min-h-0 flex-col rounded-lg border border-border bg-surface-1',
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <h2 className="text-body font-semibold">Today&apos;s calendar</h2>
          <p className="text-small text-text-secondary">
            {loading
              ? 'Loading…'
              : showHardError
                ? 'Could not load'
                : interactive
                  ? `${itemCount} item${itemCount === 1 ? '' : 's'}: drag backlog tasks here`
                  : `${itemCount} item${itemCount === 1 ? '' : 's'} scheduled`}
          </p>
        </div>
      </header>

      {showHardError ? (
        <p role="alert" className="px-4 py-6 text-body text-danger">
          Could not load today&apos;s calendar.{' '}
          <button type="button" className="underline" onClick={retryCalendar}>
            Retry
          </button>
        </p>
      ) : (
        <div
          className={cn(
            'chronos-calendar min-h-[20rem] flex-1 overflow-hidden p-2 sm:min-h-[24rem] sm:p-3',
            'h-[min(calc(60dvh-var(--chrome-bottom,0px)),32rem)] sm:h-[min(52dvh,28rem)]',
            scheduling?.dragConflicts && 'chronos-drag-conflict',
          )}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[luxonPlugin, timeGridPlugin, ...(interactive ? [interactionPlugin] : [])]}
            initialView="timeGridDay"
            headerToolbar={false}
            height="100%"
            timeZone={timezone}
            nowIndicator
            allDaySlot
            slotMinTime={minutesToSlot(dayWindow.slotMin)}
            slotMaxTime={minutesToSlot(dayWindow.slotMax)}
            scrollTime={dayWindow.scrollTime}
            slotDuration={`00:${String(SNAP_MINUTES).padStart(2, '0')}:00`}
            slotLabelInterval="00:30:00"
            snapDuration={interactive ? `00:${String(SNAP_MINUTES).padStart(2, '0')}:00` : undefined}
            slotEventOverlap={false}
            weekends
            {...CALENDAR_DRAG_PROPS}
            events={displayEvents}
            eventClick={onClick}
            eventDidMount={onEventMount}
            editable={interactive}
            droppable={interactive}
            drop={interactive ? scheduling!.onDrop : undefined}
            eventDrop={interactive ? scheduling!.onEventDrop : undefined}
            eventResize={interactive ? scheduling!.onEventResize : undefined}
            eventDragStart={
              interactive
                ? (arg) => {
                    beginDrag();
                    scheduling!.onEventDragStart(arg);
                    scheduling!.onEventDrag(arg);
                  }
                : undefined
            }
            eventDragStop={
              interactive
                ? (info) => {
                    scheduling!.onEventDragStop(info);
                    afterCalendarGesture(endDrag);
                  }
                : undefined
            }
            eventAllow={
              interactive
                ? (span, moving) => {
                    if (!moving?.id.startsWith('block-')) return true;
                    scheduling!.onEventDrag({
                      event: {
                        id: moving.id,
                        start: span.start,
                        end: span.end,
                      },
                    });
                    return true;
                  }
                : undefined
            }
            selectable={false}
          />
        </div>
      )}
    </section>
  );
}
